import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plane, BedDouble, Car, Ship, TrainFront, Bus, UtensilsCrossed, Percent,
  Stamp, Shield, ClipboardCheck, FileText, Briefcase, Lock, Wallet, LayoutGrid,
  Bell, Search, Mic, Home, Compass, MoreHorizontal, Plus, Download, X, Smartphone,
  QrCode, ChevronRight,
} from "lucide-react";

/*  AppShellMockup — interactive native-app-style icon board rendered inside a
    phone frame on the /app hero. Every tile is a real <Link> to its section,
    not a static image. Bottom nav mirrors the mockup (Home · Trips · + · Explore · More)
    with the centre + opening an "Install this app" bottom-sheet.
    Designed for a 390×844 (iPhone 15 Pro) inner canvas so it maps 1:1 to the
    home screen most users will see once installed as a PWA.               */

const TILES = [
  // Row 1 — transport
  { to: "/app/flights",  label: "Flights",   Icon: Plane,           tint: "from-sky-100 to-sky-50",         ring: "ring-sky-200",       ic: "text-sky-600" },
  { to: "/hotels",       label: "Hotels",    Icon: BedDouble,       tint: "from-fuchsia-100 to-fuchsia-50", ring: "ring-fuchsia-200",   ic: "text-fuchsia-600" },
  { to: "/ground",       label: "Cars",      Icon: Car,             tint: "from-emerald-100 to-emerald-50", ring: "ring-emerald-200",   ic: "text-emerald-600" },
  { to: "/water",        label: "Cruises",   Icon: Ship,            tint: "from-cyan-100 to-cyan-50",       ring: "ring-cyan-200",      ic: "text-cyan-600" },
  // Row 2
  { to: "/rail",         label: "Trains",    Icon: TrainFront,      tint: "from-lime-100 to-lime-50",       ring: "ring-lime-200",      ic: "text-lime-700" },
  { to: "/bus",          label: "Buses",     Icon: Bus,             tint: "from-amber-100 to-amber-50",     ring: "ring-amber-200",     ic: "text-amber-600" },
  { to: "/food-dining",  label: "Dining",    Icon: UtensilsCrossed, tint: "from-rose-100 to-rose-50",       ring: "ring-rose-200",      ic: "text-rose-600" },
  { to: "/travel-news",  label: "Deals",     Icon: Percent,         tint: "from-red-100 to-red-50",         ring: "ring-red-200",       ic: "text-red-600" },
  // Row 3 — docs & services
  { to: "/visas",        label: "Visas",     Icon: Stamp,           tint: "from-indigo-100 to-indigo-50",   ring: "ring-indigo-200",    ic: "text-indigo-600" },
  { to: "/tips",         label: "Insurance", Icon: Shield,          tint: "from-teal-100 to-teal-50",       ring: "ring-teal-200",      ic: "text-teal-600" },
  { to: "/app/checklists", label: "Checklists", Icon: ClipboardCheck, tint: "from-yellow-100 to-yellow-50", ring: "ring-yellow-200",    ic: "text-yellow-600" },
  { to: "/app/documents", label: "Documents", Icon: FileText,       tint: "from-blue-100 to-blue-50",       ring: "ring-blue-200",      ic: "text-blue-600" },
  // Row 4 — our differentiators
  { to: "/app/trips",    label: "My Trips",  Icon: Briefcase,       tint: "from-violet-100 to-violet-50",   ring: "ring-violet-200",    ic: "text-violet-600" },
  { to: "/vault",        label: "Private Vault", Icon: Lock,        tint: "from-slate-100 to-slate-50",     ring: "ring-slate-300",     ic: "text-slate-700" },
  { to: "/app/expenses", label: "Expenses",  Icon: Wallet,          tint: "from-orange-100 to-orange-50",   ring: "ring-orange-200",    ic: "text-orange-600" },
  { to: "/app",          label: "More",      Icon: LayoutGrid,      tint: "from-zinc-100 to-zinc-50",       ring: "ring-zinc-200",      ic: "text-zinc-700" },
];

function Tile({ tile }) {
  const { to, label, Icon, tint, ring, ic } = tile;
  return (
    <Link to={to} data-testid={`app-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="group flex flex-col items-center gap-1.5 outline-none focus-visible:scale-105 focus-visible:ring-2 focus-visible:ring-blue-400 rounded-2xl">
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tint} ring-1 ${ring} shadow-sm grid place-items-center transition-all group-hover:-translate-y-0.5 group-hover:shadow-md group-active:scale-95`}>
        <Icon className={`w-6 h-6 ${ic}`} strokeWidth={2.1} />
      </div>
      <div className="text-[10.5px] font-700 text-slate-800 text-center leading-tight max-w-[60px] truncate">{label}</div>
    </Link>
  );
}

export default function AppShellMockup({ qrData }) {
  const [installOpen, setInstallOpen] = useState(false);
  const nowTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });

  return (
    <div className="relative w-full overflow-hidden" data-testid="app-shell-mockup">
      {/* Ambient background bloom */}
      <div className="absolute -inset-2 sm:-inset-6 bg-gradient-to-tr from-sky-200/40 via-fuchsia-200/25 to-amber-100/40 rounded-[3rem] blur-3xl -z-10" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[380px] rounded-[3rem] border-[10px] border-slate-900 bg-slate-900 shadow-2xl overflow-hidden">
        {/* Screen */}
        <div className="rounded-[2.2rem] bg-gradient-to-b from-sky-50 via-white to-white overflow-hidden">
          {/* Dynamic Island / status bar */}
          <div className="flex items-center justify-between px-6 pt-2.5 pb-1 text-[11px] font-700 text-slate-900">
            <span>{nowTime}</span>
            <div className="w-24 h-6 rounded-full bg-slate-900 mx-2" aria-hidden="true" />
            <span className="flex items-center gap-1"><span className="tracking-tight">••••</span> <span>96%</span></span>
          </div>

          {/* Top nav */}
          <div className="px-4 pb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-emerald-400 grid place-items-center shadow-sm">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18"/></svg>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 leading-none">Good day</div>
                <div className="font-serif font-800 text-slate-900 leading-tight">TravelerPulse<span className="text-blue-600">.pro</span></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center" aria-label="Notifications"><Bell className="w-4 h-4 text-slate-600" /></button>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-rose-400 ring-2 ring-white shadow-sm" />
            </div>
          </div>

          {/* Search bar */}
          <div className="px-4 pb-3">
            <Link to="/travel-search" data-testid="app-search" className="flex items-center gap-2 w-full rounded-full bg-white/90 ring-1 ring-slate-200 px-3.5 py-2.5 text-sm text-slate-500 hover:ring-blue-300 transition-colors">
              <Search className="w-4 h-4 text-slate-400" />
              <span className="flex-1 truncate">Search destinations, hotels, flights…</span>
              <Mic className="w-4 h-4 text-slate-400" />
            </Link>
          </div>

          {/* Icon grid — 4 × 4 */}
          <div className="px-4 pb-3">
            <div className="grid grid-cols-4 gap-y-3.5 gap-x-2">
              {TILES.map((t) => <Tile key={t.label} tile={t} />)}
            </div>
          </div>

          {/* Featured card */}
          <div className="px-4 pb-2">
            <Link to="/app/trips" data-testid="app-featured-trip" className="block rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-white shadow-sm">
              <div className="relative h-24 bg-gradient-to-r from-sky-300 via-blue-400 to-emerald-400">
                <div className="absolute inset-0 flex items-end p-3">
                  <div className="text-white">
                    <div className="text-[10px] font-mono uppercase tracking-widest">Your next trip</div>
                    <div className="font-serif font-800 text-lg leading-tight">Tokyo · Cherry Blossom Season</div>
                  </div>
                </div>
              </div>
              <div className="px-3 py-2 flex items-center justify-between text-[11px] text-slate-600">
                <span>Mar 27 – Apr 5 · 10 days</span>
                <span className="inline-flex items-center gap-0.5 font-700 text-blue-700">Open <ChevronRight className="w-3 h-3" /></span>
              </div>
            </Link>
          </div>

          {/* Bottom nav */}
          <div className="mt-1 px-2 pt-2 pb-3 bg-white/80 backdrop-blur border-t border-slate-100 flex items-center justify-around">
            <NavIcon to="/app" label="Home"    Icon={Home}            active testid="nav-home" />
            <NavIcon to="/app/trips" label="Trips"  Icon={Briefcase}   testid="nav-trips" />
            <button onClick={() => setInstallOpen(true)} data-testid="nav-install"
              className="-mt-6 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 text-white grid place-items-center shadow-lg ring-4 ring-white hover:scale-105 active:scale-95 transition-transform" aria-label="Install this app on your phone">
              <Plus className="w-6 h-6" />
            </button>
            <NavIcon to="/explore-places" label="Explore" Icon={Compass}  testid="nav-explore" />
            <NavIcon to="/app" label="More"     Icon={MoreHorizontal}   testid="nav-more" />
          </div>

          {/* Home indicator */}
          <div className="h-1.5 flex justify-center pb-1"><div className="w-32 h-1 rounded-full bg-slate-900/70" /></div>
        </div>
      </div>

      {installOpen && (
        <InstallSheet qrData={qrData} onClose={() => setInstallOpen(false)} />
      )}
    </div>
  );
}

function NavIcon({ to, label, Icon, active, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-xl ${active ? "text-blue-700" : "text-slate-500 hover:text-slate-800"}`}>
      <Icon className="w-5 h-5" strokeWidth={2.1} />
      <span className={`text-[10px] font-700 ${active ? "text-blue-700" : "text-slate-500"}`}>{label}</span>
    </Link>
  );
}

function InstallSheet({ qrData, onClose }) {
  return (
    <div className="fixed inset-0 z-[9998] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="install-sheet">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-5 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
          <button onClick={onClose} data-testid="install-sheet-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center hover:ring-slate-300"><X className="w-4 h-4 text-slate-600" /></button>
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 grid place-items-center shadow-md">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <h3 className="mt-3 font-serif font-900 text-2xl text-slate-900 leading-tight">Install TravelerPulse on your phone</h3>
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">Add it to your home screen for one-tap access to trips, documents, expenses and your Private Vault — even offline.</p>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4 p-3 rounded-2xl bg-slate-50 ring-1 ring-slate-100">
            <img alt="Scan to install" className="w-24 h-24 rounded-xl bg-white ring-1 ring-slate-200 p-1"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData || "https://travelerpulse.pro/app")}`} />
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1"><QrCode className="w-3 h-3" /> Scan on your phone</div>
              <div className="font-serif font-800 text-slate-900 mt-0.5 leading-tight">One tap to preview</div>
              <div className="text-xs text-slate-500 mt-0.5">Point your camera at the code</div>
            </div>
          </div>

          <ol className="text-sm text-slate-700 space-y-2">
            <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-800 grid place-items-center shrink-0">1</span> Open <b>travelerpulse.pro/app</b> in Safari (iPhone) or Chrome (Android).</li>
            <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-800 grid place-items-center shrink-0">2</span> Tap the <b>Share</b> icon → <b>Add to Home Screen</b>.</li>
            <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-800 grid place-items-center shrink-0">3</span> Launch TravelerPulse from your home screen — icons live where you tap.</li>
          </ol>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Link to="/app" data-testid="install-sheet-open-preview" onClick={onClose}
              className="py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-700 text-center inline-flex items-center justify-center gap-1.5">
              <Download className="w-4 h-4" /> Open web preview
            </Link>
            <a href="https://travelerpulse.pro/app" data-testid="install-sheet-copy"
              onClick={(e) => { e.preventDefault(); navigator.clipboard?.writeText("https://travelerpulse.pro/app"); }}
              className="py-2.5 rounded-xl border-2 border-slate-300 hover:border-blue-500 text-slate-700 text-sm font-700 text-center">
              Copy phone link
            </a>
          </div>

          <p className="text-[11px] text-slate-500 text-center pt-2">Every icon on the home screen deep-links straight into that section — no menu digging.</p>
        </div>
      </div>
    </div>
  );
}
